"use client";

import { Button } from "@/components/ui/button";
import { useSite } from "@/lib/hooks/site";
import { cn } from "@/lib/utils";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import CalendarDaysIcon from "@heroicons/react/24/solid/CalendarDaysIcon";
import GiftTopIcon from "@heroicons/react/24/solid/GiftTopIcon";
import MapIcon from "@heroicons/react/24/solid/MapIcon";
import PhoneIcon from "@heroicons/react/24/solid/PhoneIcon";
import ShareIcon from "@heroicons/react/24/solid/ShareIcon";
import TicketIcon from "@heroicons/react/24/solid/TicketIcon";
import WhatsappButton from "@/components/WhatsappButton";
import dynamicallyCreateAnchorAndNavigate from "@/lib/navigate-url";
import {
  WHATSAPP_SECTIONS,
  WHATSAPP_TAGS,
} from "@/@core/enums/whatsapp_tags.enum";
import { useVenueDetail } from "@/hooks/venueContext";
import { ROUTES } from "@/@core/constants/routes";
import useWhatsappContext from "@/hooks/useWhatsappContext";
import getTopDiscountByVenue from "@/lib/get-top-discount-by-venue";
import WhatsappLinkWithBeacon from "@/components/WhatsappLinkWithBeacon";
import useGetWpHref from "@/hooks/useGetWpHref";
import { useTranslation } from "@/hooks/translationContext";
import {
  GLOBAL_VENUE_DISCOUNT_DESCRIPTION_KEY,
  GLOBAL_VENUE_DISCOUNT_PERCENT_KEY,
} from "@/@core/constants/discount";
import { usePlausible } from "next-plausible";
import {
  CLICK_QUOTE_EVENT_SECTIONS,
  PLAUSIBLE_GOAL_KEYS,
} from "@/@core/constants/plausible";
import debounce from "@/lib/debounce";
import { useIsAskQuestionEnabled } from "@/hooks/useIsAskQuestionEnabled";
import WhatsappIcon from "@/components/hero-icons/whatsapp-icon";
import dynamic from "next/dynamic";
import CompanyDiscountLinkButton from "@/components/views/company-discounts/CompanyDiscountLinkButton";
import { isPhysiotherapistVenue } from "@/lib/venue-business-type";

const VenueShareModal = dynamic(
  () => import("@/components/views/modals/venue/ShareModal"),
);
const VenueDetailShareModal = dynamic(
  () => import("@/components/views/venue-details/new-details/share/Modal"),
);

interface Props {
  phone: string;
  links: any[];
  hasCampaign: boolean;
  hasEvent: boolean;
  datas: {
    phone: string;
    title: string;
    id: number;
    banners: { url: string }[];
    location: string;
    tags: { title: string }[];
    logo: string;
    clickAdressLink: string;
    whatsappPhoneNumber: string;
  };
  discount: {
    percent: number;
    descriptions: [
      {
        title: string;
        description: string;
      },
    ];
  }[];
  data: any;
}

export default function PlaceDetailsTabs(props: Props) {
  const { data, links } = props;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { t } = useTranslation();

  const [hash, setHash] = useState("");
  const [isOpen, setOpen] = useState(false);
  const [share, setShare] = useState(false);

  const { venue } = useVenueDetail();
  const whatsappContext = useWhatsappContext();
  const getWhatsappHrefUrl = useGetWpHref();

  const router = useRouter();
  const site = useSite();
  const plausible = usePlausible();
  const isAskQuestionEnabled = useIsAskQuestionEnabled();
  // Physiotherapist venues carry no quote, discount, campaign or company
  // discount CTAs (SAB-362).
  const isPhysiotherapist = isPhysiotherapistVenue(data);

  const handleScroll = useCallback(() => {
    if (!hash) return;
    if (window.scrollY >= window.innerHeight - 225) return;

    const scrollPosition = window.scrollY;

    const shouldResetHash =
      (window.location.hash === "#comments" && scrollPosition < 60) ||
      hash !== "#comments";

    if (shouldResetHash) {
      if (scrollPosition < window.innerHeight - 225) {
        setHash("");
        history.replaceState(null, '', window.location.pathname + window.location.search);
      }
    }
  }, [hash]);

  useEffect(() => {
    const optimizedHandleScroll = debounce(handleScroll, 200);

    window.addEventListener("scroll", optimizedHandleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", optimizedHandleScroll);
    };
  }, [handleScroll]);

  const visibleLinks = links.filter((link) => {
    if (link.key == "company-discounts") {
      return site?.setting?.disabledPages?.company !== 1;
    }

    return true;
  });

  const defaultSortOrder = visibleLinks.map((link, index) => ({
    key: link.key,
    sort_order: index + 1,
  }));

  const getSortOrder = (key: string) => {
    const realSortOrder = site.tabs
      ? site.tabs.from_site_tabs.find((tab) => tab.type === key)?.sort_order ||
      0
      : 0;
    const defaultOrder =
      defaultSortOrder.find((order) => order.key === key)?.sort_order || 0;
    return realSortOrder || defaultOrder;
  };

  const sorted = visibleLinks.sort((a, b) => {
    const sortOrderA = getSortOrder(a.key);
    const sortOrderB = getSortOrder(b.key);

    if (sortOrderA === sortOrderB) {
      return -1;
    }

    return sortOrderA - sortOrderB;
  });

  const handleButtonClick = (link: any) => {
    setHash(link.id);
    const targetElement = document.getElementById(link.id.replace("#", ""));
    const position = targetElement?.getBoundingClientRect();
    if (!position) return;

    window.scrollTo(position.left, position.top + window.scrollY - 100);
  };

  function MenuTab({ link, isActive }) {
    return (
      <button
        role="button"
        onClick={() => handleButtonClick(link)}
        className={cn(
          "max-sm:pb-4 max-sm:pt-1 max-sm:px-4 md:py-6 md:px-2 relative",
          "after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-full after:h-[2px] after:bg-secondary after:transition-all after:duration-300",
          isActive
            ? "text-secondary after:opacity-100"
            : "text-primary after:opacity-0",
        )}
      >
        {link.title}
      </button>
    )
  }


  const globalDiscountPercent = t(GLOBAL_VENUE_DISCOUNT_PERCENT_KEY);
  const globalDiscountDescription = t(GLOBAL_VENUE_DISCOUNT_DESCRIPTION_KEY);

  const hasDiscount = () => {
    if (isPhysiotherapist) return false;

    const activeDiscounts = props.discount.find(d => d?.percent == 0);
    if (activeDiscounts) return false;

    return (
      props.discount.length ||
      globalDiscountPercent !== GLOBAL_VENUE_DISCOUNT_PERCENT_KEY
    );
  };

  const getWhatsappContext = () => {
    return whatsappContext.getContext({
      venue: venue.descriptions?.[0]?.title,
      "discount-visitors": props.discount?.[0]
        ? props.discount?.[0]?.percent
        : globalDiscountPercent,
    });
  };

  const getDiscountPercent = () => {
    return props.discount?.[0]
      ? props.discount?.[0].percent
      : globalDiscountPercent;
  };

  const sendPlausibleEvent = () => {
    plausible(PLAUSIBLE_GOAL_KEYS.CLICK_QUOTE_EVENT, {
      props: {
        section: CLICK_QUOTE_EVENT_SECTIONS.VENUE_DETAIL_HEADER_SECTION,
      },
    });
  };

  return (
    <>
      {share && (
        <VenueShareModal isOpen={share} onClose={() => setShare(false)} />
      )}
      {isOpen && (
        <VenueDetailShareModal
          isShareOpen={isOpen}
          onClose={() => setOpen(false)}
        />
      )}

      <div className="sticky top-0 bg-white z-20">
        <div className="flex items-start relative container z-20 bg-white top-0 min-h-full justify-between md:overflow-x-clip overflow-x-auto none-scrollbar border-b border-assistant-secondary">
          <div className="flex bg-white max-md:px-2 gap-2 text-sm md:overflow-x-clip overflow-x-auto none-scrollbar font-bold text-primary relative">
            {sorted.map((link, index) => {
              if (link) {
                return (
                  <MenuTab key={index} link={link} isActive={hash === link.id || (link.id === sorted[0]?.id && hash === "")} />
                )
              }
            })}
          </div>

          <div className="absolute z-10 top-24 right-0 max-w-[285px] gap-[10px] md:flex hidden flex-col">
            <div className="flex rounded-2xl flex-col gap-2 p-4 bg-white shadow-lg text-sm">
              {!isPhysiotherapist && (
                <CompanyDiscountLinkButton
                  venueId={props.datas?.id}
                  className="w-[253px]"
                  showIcon={false}
                />
              )}

              <WhatsappButton
                translateValues={{
                  "discount-visitors": getTopDiscountByVenue(
                    venue as Venue.List,
                  ),
                }}
                section={WHATSAPP_SECTIONS.VENUE_DETAIL}
                tag={WHATSAPP_TAGS.NORMAL}
                phoneNumber={props.datas.whatsappPhoneNumber}
                venue={venue as Venue.List}
                variant="default"
                className="bg-[#25D366] hover:bg-[#25b366] rounded-full text-white flex items-center justify-center
                                    px-[21px] py-3 text-sm w-[253px] text-center relative"
              >
                <WhatsappIcon className="absolute left-4 size-4" />{" "}
                <span className="ml-2">Whatsapp ile Mesaj Gönder</span>
              </WhatsappButton>

              <Button
                onClick={() => setOpen(true)}
                variant="assistant"
                className="relative rounded-full flex justify-center items-center gap-4 px-[21px] py-3"
              >
                <PhoneIcon className="size-4 absolute left-4" /> Telefon Et
              </Button>

              {isAskQuestionEnabled && !isPhysiotherapist && (
                <Button
                  onClick={() => {
                    sendPlausibleEvent();

                    const districtSlug = props?.data?.district?.slug;
                    router.push(
                      ROUTES.CONTACT_WITH_VENUES + `?district=${districtSlug}`,
                    );
                  }}
                  variant="gradient"
                  className="px-[21px] py-3 rounded-full flex justify-center"
                >
                  {t("quote-button-title")}
                </Button>
              )}

              {props.datas?.whatsappPhoneNumber && hasDiscount() && (
                <WhatsappLinkWithBeacon
                  venueId={props.datas?.id}
                  section={WHATSAPP_SECTIONS.DISCOUNT}
                  title={`%${getDiscountPercent()} İndirimi Sor`}
                  disabled={!props.datas?.whatsappPhoneNumber}
                  href={getWhatsappHrefUrl({
                    tag: WHATSAPP_TAGS.DISCOUNT,
                    phone: props.datas.whatsappPhoneNumber,
                    translateValues: getWhatsappContext(),
                  })}
                >
                  <div className="flex gap-2 items-center font-[500] rounded-lg hover:bg-slate-100 duration-200 p-1 w-full">
                    <GiftTopIcon className="size-6" />%
                    {props.discount?.[0]
                      ? props.discount?.[0].percent
                      : globalDiscountPercent}{" "}
                    İndirimi Sor
                  </div>
                </WhatsappLinkWithBeacon>
              )}

              {props.datas?.whatsappPhoneNumber && props.hasCampaign && (
                <WhatsappLinkWithBeacon
                  section={WHATSAPP_SECTIONS.CAMPAIGN}
                  venueId={props.datas?.id}
                  title={t("venue-detail-ask-campaign-button")}
                  disabled={!props.datas?.whatsappPhoneNumber}
                  href={getWhatsappHrefUrl({
                    tag: WHATSAPP_TAGS.CAMPAIGN,
                    phone: props.datas.whatsappPhoneNumber,
                    translateValues: getWhatsappContext(),
                  })}
                >
                  <div className="flex gap-2 items-center font-[500] rounded-lg hover:bg-slate-100 duration-200 p-1 w-full">
                    <TicketIcon className="size-6" /> Kampanyaları Sor
                  </div>
                </WhatsappLinkWithBeacon>
              )}

              {props.hasEvent && props.datas?.whatsappPhoneNumber && (
                <WhatsappLinkWithBeacon
                  section={WHATSAPP_SECTIONS.EVENT}
                  venueId={props.datas?.id}
                  title={t("venue-detail-ask-event-button")}
                  disabled={!props.datas?.whatsappPhoneNumber}
                  href={getWhatsappHrefUrl({
                    tag: WHATSAPP_TAGS.EVENT,
                    phone: props.datas.whatsappPhoneNumber,
                    translateValues: getWhatsappContext(),
                  })}
                >
                  <div className="flex gap-2 items-center font-[500] rounded-lg hover:bg-slate-100 duration-200 p-1 w-full">
                    <CalendarDaysIcon className="size-6" /> Etkinlikleri Sor
                  </div>
                </WhatsappLinkWithBeacon>
              )}

              <button
                role="button"
                className="flex gap-2 items-center font-[500] rounded-lg hover:bg-slate-100 duration-200 p-1 w-full"
                onClick={() => {
                  setShare(true);
                }}
              >
                <ShareIcon className="size-6" /> Arkadaşına Gönder
              </button>

              <button
                role="button"
                className="flex gap-2 items-center font-[500] rounded-lg hover:bg-slate-100 duration-200 p-1 w-full"
                onClick={() =>
                  dynamicallyCreateAnchorAndNavigate(
                    props.datas.clickAdressLink,
                  )
                }
              >
                <MapIcon className="size-6" /> Yol Tarifi Al
              </button>
            </div>

            {props.datas?.whatsappPhoneNumber && hasDiscount() && (
              <WhatsappLinkWithBeacon
                section={WHATSAPP_SECTIONS.DISCOUNT}
                venueId={props.datas?.id}
                title={`%${getDiscountPercent()} İndirim`}
                disabled={!props.datas?.whatsappPhoneNumber}
                href={getWhatsappHrefUrl({
                  tag: WHATSAPP_TAGS.DISCOUNT,
                  phone: props.datas.whatsappPhoneNumber,
                  translateValues: getWhatsappContext(),
                })}
              >
                <div>
                  <div className="flex items-center rounded-2xl justify-between w-[285px] gap-2 p-4 bg-white shadow-lg text-xs">
                    <div className="flex flex-col gap-1 justify-center items-center rounded-2xl border border-slate-300 min-w-[76px] h-16 p-2">
                      <span className="text-slate-800 text-2xl font-bold font-['DM Sans']">
                        %{getDiscountPercent()}
                      </span>
                      <span className="text-slate-800 text-xs font-medium font-['DM Sans']">
                        İndirim
                      </span>
                    </div>
                    {props.discount?.[0]?.descriptions?.[0].description ? (
                      <div
                        className="h-20 overflow-hidden max-w-[164px] text-ellipsis line-clamp-3"
                        dangerouslySetInnerHTML={{
                          __html: props.discount[0].descriptions[0].description,
                        }}
                      />
                    ) : (
                      <span className="h-16 max-w-[164px] flex flex-wrap gap-[3px]">
                        {globalDiscountDescription}
                      </span>
                    )}
                    {/*<span className="h-16 max-w-[164px] flex flex-wrap gap-[3px]">*/}
                    {/*  {props.discount?.[0] ? props.discount[0].descriptions[0].description*/}
                    {/*    .split(" ")*/}
                    {/*    .map((word, i) => (*/}
                    {/*      <span key={i}>{word}</span>*/}
                    {/*    )) : globalDiscountDescription}*/}
                    {/*</span>*/}
                    {/* )} */}
                  </div>
                </div>
              </WhatsappLinkWithBeacon>
            )}
          </div>
        </div>

        <div className="absolute left-0 right-0 h-[1px] bg-assistant-secondary" />
      </div>
    </>
  );
}
